import * as React from "react"
import * as Yup from "yup"
import { CmsVendor } from "@modules/graphql/types"
import { useCosmicCmsBucketsForIntegrationQuery } from "@modules/cms/shared/queries.generated"
import { RadioButtonConnectedField } from "gatsby-interface"
import Form from "@modules/form/components/Form"
import { composeIntegrationConfig, getInitialDataSource } from "../utils"
import { ConnectCmsFormProps } from "../types"
import { ConnectCmsFormControls } from "./ConnectCmsFormControls"
import { useSetCmsIntegration } from "../hooks/useSetCmsIntegration"
import { CmsDataType } from "@modules/cms/shared/utils"
import Loading from "@modules/ui/components/Loading"
import { ConnectCmsFormLayout } from "./ConnectCmsFormLayout"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { cmsIntegration as cmsIntegrationText } from "@modules/locales/default.js"
import { commonMessageTags, FormattedMessage } from "@modules/locales"

type FormValues = {
  dataSource: string
}

const validationSchema = Yup.object().shape<FormValues>({
  dataSource: Yup.string().required(
    cmsIntegrationText.messages.validationCosmicJsBucketRequired
  ),
})

export type ConnectCosmicJSFormProps = ConnectCmsFormProps

export function ConnectCosmicJSForm({
  siteId,
  siteName,
  onFetchError,
  onComplete,
  onCancel,
}: ConnectCosmicJSFormProps) {
  const { data, loading, error } = useCosmicCmsBucketsForIntegrationQuery({
    variables: {
      siteId,
    },
    onError: onFetchError,
  })
  const [setCmsIntegration] = useSetCmsIntegration({
    siteId,
  })

  if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  if (loading && data === undefined) {
    return (
      <Loading
        delay={1000}
        message={cmsIntegrationText.messages.loadingCosmicJsBuckets}
      />
    )
  }

  const buckets = data?.cosmicCMSBucketsForIntegration ?? []
  const options = buckets.map(({ id, name }) => ({
    value: id,
    label: name,
  }))

  return (
    <Form<FormValues>
      initialValues={{
        dataSource: getInitialDataSource(buckets),
      }}
      validationSchema={validationSchema}
      onSubmit={values => {
        return setCmsIntegration({
          variables: {
            config: composeIntegrationConfig(
              CmsDataType.CosmicJsBucketSlug,
              buckets,
              values.dataSource
            ),
            siteId,
            vendor: CmsVendor.Cosmicjs,
          },
        }).finally(onComplete)
      }}
      enableReinitialize
    >
      {({ isSubmitting }) => {
        return (
          <ConnectCmsFormLayout
            description={
              <FormattedMessage<"siteName", "strong">
                message={cmsIntegrationText.messages.chooseCosmicJsDataSource}
                values={{ siteName }}
                tags={commonMessageTags}
              />
            }
            controls={
              <ConnectCmsFormControls
                isSubmitting={isSubmitting}
                onCancel={onCancel}
              />
            }
          >
            <RadioButtonConnectedField
              name="dataSource"
              label={cmsIntegrationText.labels.cosmicJsBucket}
              options={options}
              variant="framed"
            />
          </ConnectCmsFormLayout>
        )
      }}
    </Form>
  )
}
